/**
 * AI メニュー開発 (v0.3): 右パネル。対話から抽出された開発条件 (context_slots) の表示・手動編集。
 * 詳細: docs/design/v03-ai-menu-dev-improvements.md §6.2
 *
 * 実装スコープメモ: Design C-2 の「AI更新直後30秒highlight」は実装しているが、
 * 「元に戻す」ボタンは、変更前値を復元するための監査ログ参照APIが未整備のため本パスでは
 * 見送り、highlightのみで「AIが更新した」ことを伝える (フォローアップ候補としてOpen Items相当)。
 */
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Modal } from '@/components/Modal';

export type ContextSlots = {
  target_category_id?: string | null;
  target_customer_note?: string | null;
  price_min_yen?: number | null;
  price_max_yen?: number | null;
  cost_target_type?: 'ratio' | 'amount' | null;
  cost_target_value?: number | null;
  production_servings?: number | null;
  wishes_free_text?: string | null;
  auto_mode?: boolean;
  updated_by?: 'ai' | 'user' | 'system';
  updated_at?: string;
};

export type IndustryTemplateSummary = { template_id: string; template_name: string } | null;
export type MenuCategoryOption = { menu_category_id: string; category_name: string };

const DEBOUNCE_MS = 800;
const HIGHLIGHT_MS = 30_000;

const fieldsDiffer = (a: ContextSlots, b: ContextSlots): (keyof ContextSlots)[] => {
  const keys: (keyof ContextSlots)[] = [
    'target_category_id', 'target_customer_note', 'price_min_yen', 'price_max_yen',
    'cost_target_type', 'cost_target_value', 'production_servings', 'wishes_free_text', 'auto_mode',
  ];
  return keys.filter((k) => JSON.stringify(a[k] ?? null) !== JSON.stringify(b[k] ?? null));
};

export function ContextPanel({
  sessionId, contextSlots, industryTemplate, industryTemplates, categories, onPatched,
}: {
  sessionId: string;
  contextSlots: ContextSlots;
  industryTemplate: IndustryTemplateSummary;
  industryTemplates: Array<{ template_id: string; template_name: string }>;
  categories: MenuCategoryOption[];
  onPatched: () => void;
}) {
  const [draft, setDraft] = useState<ContextSlots>(contextSlots);
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set());
  const [industryModalOpen, setIndustryModalOpen] = useState(false);
  const [pendingIndustryId, setPendingIndustryId] = useState<string | null>(null);
  const prevSlotsRef = useRef<ContextSlots>(contextSlots);
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // Designレビュー MEDIUM反映 (2026-08-04): AI応答待ち中のポーリング再取得(4秒間隔)が、
  // ユーザーがまさに入力中の下書き(デバウンス800ms未確定分)を上書きしないよう、
  // いずれかのフィールドにフォーカスがある間は親からの同期を止める
  const isEditingRef = useRef(false);

  // 親からのcontextSlots更新 (AI由来 or 別画面でのPATCH反映) を検知し、AI更新分をhighlightする
  useEffect(() => {
    if (isEditingRef.current) return;
    const prev = prevSlotsRef.current;
    if (contextSlots.updated_by === 'ai' && contextSlots.updated_at !== prev.updated_at) {
      const changed = fieldsDiffer(prev, contextSlots);
      if (changed.length > 0) {
        setHighlighted(new Set(changed));
        const timer = setTimeout(() => setHighlighted(new Set()), HIGHLIGHT_MS);
        prevSlotsRef.current = contextSlots;
        setDraft(contextSlots);
        return () => clearTimeout(timer);
      }
    }
    prevSlotsRef.current = contextSlots;
    setDraft(contextSlots);
  }, [contextSlots]);


  const patch = async (body: Partial<ContextSlots> & { industry_template_id?: string | null }) => {
    try {
      await apiFetch(`/menu-chat/sessions/${sessionId}/context`, { method: 'PATCH', body: JSON.stringify(body) });
      onPatched();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '開発条件の更新に失敗しました');
    }
  };

  const scheduleDebouncedPatch = (field: string, body: Partial<ContextSlots>) => {
    if (debounceTimers.current[field]) clearTimeout(debounceTimers.current[field]);
    debounceTimers.current[field] = setTimeout(() => patch(body), DEBOUNCE_MS);
  };

  const flushDebounced = (field: string, body: Partial<ContextSlots>) => {
    if (debounceTimers.current[field]) clearTimeout(debounceTimers.current[field]);
    patch(body);
  };

  const highlightCls = (field: string) => (highlighted.has(field) ? 'ring-2 ring-alert-border bg-alert-bg' : '');

  const confirmIndustryChange = async () => {
    await patch({ industry_template_id: pendingIndustryId });
    setIndustryModalOpen(false);
    setPendingIndustryId(null);
  };

  return (
    <div className="flex flex-col gap-4 p-4 overflow-y-auto">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-main">開発条件</h2>
        <label className="flex items-center gap-1.5 text-xs text-text-sub">
          お任せ
          <input
            type="checkbox"
            checked={Boolean(draft.auto_mode)}
            onChange={(e) => { const next = { ...draft, auto_mode: e.target.checked }; setDraft(next); flushDebounced('auto_mode', { auto_mode: e.target.checked }); }}
            className="accent-brand-red"
            aria-describedby="auto-mode-desc"
          />
        </label>
      </div>
      <p id="auto-mode-desc" className="text-xs text-text-sub -mt-3">入力済みの条件は尊重したまま、未指定項目の質問を省略します</p>

      <div>
        <span className="block text-xs text-text-sub mb-1">業態</span>
        <div className="flex items-center gap-2">
          <span className="text-sm text-text-main">{industryTemplate?.template_name ?? '指定なし'}</span>
          <button
            type="button"
            onClick={() => { setPendingIndustryId(industryTemplate?.template_id ?? null); setIndustryModalOpen(true); }}
            className="text-xs text-brand-red hover:underline"
          >
            変更
          </button>
        </div>
      </div>

      <div className={`rounded-md p-1.5 -m-1.5 transition-colors ${highlightCls('target_category_id')}`}>
        <label htmlFor="ctx-category" className="block text-xs text-text-sub mb-1">対象カテゴリ</label>
        <select
          id="ctx-category"
          value={draft.target_category_id ?? ''}
          onChange={(e) => { const v = e.target.value || null; setDraft({ ...draft, target_category_id: v }); flushDebounced('target_category_id', { target_category_id: v }); }}
          className="w-full border border-surface-border rounded px-2 py-1.5 text-sm bg-white"
        >
          <option value="">(未設定)</option>
          {categories.map((c) => <option key={c.menu_category_id} value={c.menu_category_id}>{c.category_name}</option>)}
        </select>
      </div>

      <div className={`rounded-md p-1.5 -m-1.5 transition-colors ${highlightCls('target_customer_note')}`}>
        <label htmlFor="ctx-customer" className="block text-xs text-text-sub mb-1">想定するお客様</label>
        <input
          id="ctx-customer"
          type="text"
          maxLength={200}
          value={draft.target_customer_note ?? ''}
          onChange={(e) => { const v = e.target.value; setDraft({ ...draft, target_customer_note: v }); scheduleDebouncedPatch('target_customer_note', { target_customer_note: v || null }); }}
          onBlur={(e) => flushDebounced('target_customer_note', { target_customer_note: e.target.value || null })}
          className="w-full border border-surface-border rounded px-2 py-1.5 text-sm"
        />
      </div>

      <div className={`rounded-md p-1.5 -m-1.5 transition-colors ${highlightCls('price_min_yen') || highlightCls('price_max_yen')}`}>
        <span className="block text-xs text-text-sub mb-1">目標販売価格帯 (税込)</span>
        <div className="flex items-center gap-1.5">
          <input
            type="number" min={0} max={1_000_000}
            value={draft.price_min_yen ?? ''}
            onChange={(e) => { const v = e.target.value === '' ? null : Number(e.target.value); setDraft({ ...draft, price_min_yen: v }); scheduleDebouncedPatch('price_min_yen', { price_min_yen: v }); }}
            onBlur={(e) => flushDebounced('price_min_yen', { price_min_yen: e.target.value === '' ? null : Number(e.target.value) })}
            className="w-20 border border-surface-border rounded px-2 py-1.5 text-sm"
            aria-label="目標販売価格帯 下限"
          />
          <span className="text-text-sub">〜</span>
          <input
            type="number" min={0} max={1_000_000}
            value={draft.price_max_yen ?? ''}
            onChange={(e) => { const v = e.target.value === '' ? null : Number(e.target.value); setDraft({ ...draft, price_max_yen: v }); scheduleDebouncedPatch('price_max_yen', { price_max_yen: v }); }}
            onBlur={(e) => flushDebounced('price_max_yen', { price_max_yen: e.target.value === '' ? null : Number(e.target.value) })}
            className="w-20 border border-surface-border rounded px-2 py-1.5 text-sm"
            aria-label="目標販売価格帯 上限"
          />
          <span className="text-text-sub text-sm">円</span>
        </div>
      </div>

      <div className={`rounded-md p-1.5 -m-1.5 transition-colors ${highlightCls('cost_target_type') || highlightCls('cost_target_value')}`}>
        <span className="block text-xs text-text-sub mb-1">原価目標</span>
        <div className="flex items-center gap-3 mb-1.5">
          {(['ratio', 'amount'] as const).map((type) => (
            <label key={type} className="flex items-center gap-1 text-xs text-text-body">
              <input
                type="radio" name="cost-target-type" checked={draft.cost_target_type === type}
                onChange={() => { setDraft({ ...draft, cost_target_type: type }); flushDebounced('cost_target_type', { cost_target_type: type }); }}
                className="accent-brand-red"
              />
              {type === 'ratio' ? '原価率' : '原価額'}
            </label>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <input
            type="number" min={0} max={draft.cost_target_type === 'ratio' ? 100 : 1_000_000}
            value={draft.cost_target_value ?? ''}
            disabled={!draft.cost_target_type}
            onChange={(e) => { const v = e.target.value === '' ? null : Number(e.target.value); setDraft({ ...draft, cost_target_value: v }); scheduleDebouncedPatch('cost_target_value', { cost_target_value: v }); }}
            onBlur={(e) => flushDebounced('cost_target_value', { cost_target_value: e.target.value === '' ? null : Number(e.target.value) })}
            className="w-24 border border-surface-border rounded px-2 py-1.5 text-sm disabled:opacity-50"
            aria-label="原価目標の値"
          />
          <span className="text-text-sub text-sm">{draft.cost_target_type === 'ratio' ? '%' : '円'}</span>
        </div>
      </div>

      <div className={`rounded-md p-1.5 -m-1.5 transition-colors ${highlightCls('production_servings')}`}>
        <label htmlFor="ctx-servings" className="block text-xs text-text-sub mb-1">仕込み人数</label>
        <div className="flex items-center gap-1.5">
          <input
            id="ctx-servings" type="number" min={1} max={500}
            value={draft.production_servings ?? ''}
            onChange={(e) => { const v = e.target.value === '' ? null : Number(e.target.value); setDraft({ ...draft, production_servings: v }); scheduleDebouncedPatch('production_servings', { production_servings: v }); }}
            onBlur={(e) => flushDebounced('production_servings', { production_servings: e.target.value === '' ? null : Number(e.target.value) })}
            className="w-20 border border-surface-border rounded px-2 py-1.5 text-sm"
          />
          <span className="text-text-sub text-sm">人前</span>
        </div>
      </div>

      <div className={`rounded-md p-1.5 -m-1.5 transition-colors ${highlightCls('wishes_free_text')}`}>
        <label htmlFor="ctx-wishes" className="block text-xs text-text-sub mb-1">こんなメニューがよい</label>
        <textarea
          id="ctx-wishes" rows={3} maxLength={500}
          value={draft.wishes_free_text ?? ''}
          onChange={(e) => { const v = e.target.value; setDraft({ ...draft, wishes_free_text: v }); scheduleDebouncedPatch('wishes_free_text', { wishes_free_text: v || null }); }}
          onBlur={(e) => flushDebounced('wishes_free_text', { wishes_free_text: e.target.value || null })}
          className="w-full border border-surface-border rounded px-2 py-1.5 text-sm resize-none"
        />
      </div>

      {contextSlots.updated_at && (
        <div className="flex items-center gap-1.5 text-xs text-text-muted">
          <Sparkles size={12} />
          最終更新: {contextSlots.updated_by === 'ai' ? 'AIが自動抽出' : contextSlots.updated_by === 'user' ? '手動編集' : '-'}
          {' '}({new Date(contextSlots.updated_at).toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })})
        </div>
      )}

      <Modal
        open={industryModalOpen}
        title="業態を変更しますか?"
        role="alertdialog"
        onClose={() => setIndustryModalOpen(false)}
        closeOnOverlay={false}
        footer={
          <>
            <button type="button" onClick={() => setIndustryModalOpen(false)} className="px-4 py-2 rounded border border-surface-border hover:bg-surface-sunken">キャンセル</button>
            <button
              type="button"
              onClick={confirmIndustryChange}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-brand-orange text-white font-medium hover:opacity-90"
            >
              <AlertTriangle size={16} /> 変更する
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-text-body flex items-start gap-2">
            <AlertTriangle size={16} className="text-brand-orange shrink-0 mt-0.5" />
            今後の新規提案から新しい業態のグループ構成に切り替わります。これまでのカード表示は変わりません。よろしいですか?
          </p>
          <div>
            <label htmlFor="ctx-industry-select" className="block text-sm text-text-sub mb-1">新しい業態</label>
            <select
              id="ctx-industry-select"
              value={pendingIndustryId ?? ''}
              onChange={(e) => setPendingIndustryId(e.target.value || null)}
              className="w-full border border-surface-border rounded px-2 py-1.5 text-sm"
            >
              <option value="">業態指定なし</option>
              {industryTemplates.map((t) => <option key={t.template_id} value={t.template_id}>{t.template_name}</option>)}
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
}
